import { flip, FloatingPortal, offset, Placement, useFloating } from '@floating-ui/react-dom-interactions'
import cx from 'classnames'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { ShortcutAwareTooltip, ShortcutAwareTooltipProps } from 'common/Prose/utils/ShortcutAwareTooltip'
import { observer } from 'mobx-react-lite'
import React, { ReactNode, useContext, useMemo } from 'react'
import { ReactComponent as DownIcon } from '../icons/chevronDown.svg'
import { ReactComponent as UpIcon } from '../icons/chevronUp.svg'
import Styles from './SimpleDropdown.module.scss'
import { ButtonProps } from '../Buttons/Button'
import { SingleMenuDropdownContext, SingleMenuDropdownStore } from '../SingleMenuDropdown/SingleMenuDropdownStore'
import { useForwardRef } from 'hooks/useForwardRef'
import {
  SingleMenuAnchorButton,
  SingleMenuDropdown,
  SingleMenuDropdownBody,
} from '../SingleMenuDropdown/SingleMenuDropdown'
import { shift } from '@floating-ui/core'

export interface SimpleDropdownProps extends ButtonProps {
  children: ReactNode
  vertical?: boolean
  horizontal?: boolean
  buttonClassName?: string
  bodyClassName?: string
  label?: string
  active?: boolean
  selected?: boolean
  clickAction?: () => void
  hasChevron?: boolean
  ignoreHover?: boolean
  disabled?: boolean
  unStyledButton?: boolean
  unStyledBody?: boolean
  isSubmenu?: boolean
  placement?: Placement
  tooltip?: ShortcutAwareTooltipProps
  offsetOverride?: { mainAxis: number; crossAxis: number }
}

// TODO: Simplify, remove most of the positioning functions
export const SimpleDropdown = observer(function SimpleDropdown({
  children,
  vertical,
  horizontal,
  buttonClassName = '',
  bodyClassName = '',
  label,
  clickAction,
  placement = 'bottom',
  hasChevron = true,
  ignoreHover = false,
  unStyledButton = false,
  unStyledBody = false,
  isSubmenu = false,
  active,
  tooltip,
  offsetOverride,
  ...buttonProps
}: SimpleDropdownProps) {
  if (React.Children.count(children) !== 2) {
    throw new Error(`Use SimpleDropdown with two children`)
  }
  const [buttonChild, dropdownChild] = React.Children.toArray(children)
  const parentDropdownStore = useContext(SingleMenuDropdownContext)
  const dropdownStore = useMemo(() => new SingleMenuDropdownStore(parentDropdownStore), [parentDropdownStore])

  const { x, y, reference, floating, strategy } = useFloating({
    strategy: 'fixed',
    placement,
    middleware: [offset(offsetOverride ?? 4), flip(), shift()],
  })
  const buttonRef = useForwardRef<HTMLButtonElement>(reference)

  vertical = !horizontal

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    // Stoping propagation prevents it from interfering with the click blur
    // Which caused a bug with the chevron as by the time we process it is no longer a child
    // (we swap up/down on isOpen, so the race condition)
    e.stopPropagation()
    if (isSubmenu) return

    dropdownStore.hardToggle()
    clickAction?.()
  }

  const handleHover = !!isSubmenu ? dropdownStore.softOpen : () => {}

  const button = (
    <ConditionalWrapper
      condition={tooltip != null}
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      wrapper={(e) => <ShortcutAwareTooltip {...tooltip!}>{e}</ShortcutAwareTooltip>}
    >
      <SingleMenuAnchorButton
        blurToleranceMs={150}
        unStyled={unStyledButton}
        aria-label={label}
        className={cx(
          {
            [Styles.dropdownButton]: !unStyledButton,
            [Styles.chevronButton]: hasChevron && !isSubmenu,
            [Styles.active]: active,
            [Styles.isOpen]: dropdownStore.isOpen,
            [Styles.isSubmenu]: isSubmenu,
          },
          buttonClassName,
        )}
        onClick={handleClick}
        onMouseOver={handleHover}
        ref={buttonRef}
        {...buttonProps}
      >
        {buttonChild}
        {hasChevron && !isSubmenu && (dropdownStore.isOpen ? <UpIcon /> : <DownIcon />)}
      </SingleMenuAnchorButton>
    </ConditionalWrapper>
  )

  return (
    <SingleMenuDropdown store={dropdownStore}>
      {button}
      <FloatingPortal id="floating-ui-root">
        <SingleMenuDropdownBody
          ref={floating}
          className={cx(bodyClassName, {
            [Styles.dropdownBody]: !unStyledBody,
            [Styles.dropdownBodyVisible]: true,
            [Styles.dropdownBodyVertical]: vertical,
          })}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
        >
          {dropdownChild}
        </SingleMenuDropdownBody>
      </FloatingPortal>
    </SingleMenuDropdown>
  )
})
